/**
 * What a caller may choose about a mission, and everything the generators
 * derive from it once.
 *
 * Every field is optional except the seed: a mission select screen can hand
 * over nothing but a number and get a complete, era-consistent sortie back,
 * while a test (or a future Flight Assignments screen) can pin the era, the
 * region, the aeroplane or the ace and have the rest drawn around them.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import type { CombatLevel } from "../combat/flak.ts";
import type { AmmunitionType } from "../combat/targets.ts";
import type { FlightModelTier, RealismSettings } from "../flight/tiers.ts";
import { MissionDraw, drawWeather } from "./mission-build.ts";
import type { Formation } from "./formations.ts";
import { opposingNation, scoutsFor, type Nation } from "./mission-roster.ts";
import {
  DEFAULT_FRONT_LINE,
  MISSION_REGIONS,
  regionHasFrontLine,
  type FrontLine,
  type MissionEra,
  type MissionRegion,
  type MissionWeather,
  type SkyCondition,
  type TimeOfDay,
  type WindStrength,
} from "./mission-world.ts";

export interface MissionOptions {
  readonly seed: number;
  readonly era?: MissionEra;
  readonly region?: MissionRegion;
  /** The side the player flies for. Defaults to Allied, as the original's single missions do. */
  readonly nation?: Nation;
  readonly playerAircraft?: AircraftKey;
  /**
   * The manual's "The type of aircraft your enemy's flight group will use" —
   * one option covers every generator, including Dogfight a Famous Ace, where
   * it plays the manual's own example straight ("you can put the Red Baron
   * into a Sopwith Camel and see how he handles it"): the ace keeps his name
   * and his note, only the aeroplane under him changes. Left unset, each
   * generator draws it the way it always has.
   */
  readonly enemyAircraft?: AircraftKey;
  /**
   * The manual's "The type of ammo used ... Regular and Incendiary", set on
   * the Flight Assignments screen. Applies to the player's own flight (and
   * any wingmen with him); left unset, a generator keeps its own default —
   * incendiary for the two anti-airship/balloon strikes, regular everywhere
   * else, exactly as before this option existed.
   */
  readonly ammunition?: AmmunitionType;
  /**
   * Roadmap 11s — Weather Selection. The original's Mission Setup sentence
   * boxes the winds and sky fields the same way it boxes the aeroplane and the
   * region (measured live: `re-sessions/calib/shots/nav-windpopup.png`), and
   * the same capture shows the time-of-day field boxed too, so this port
   * offers exactly what the original does and no more — the `CHOOSE` popup's
   * own three winds (`still`/`light`/`strong`; `stormy` exists in `RB.EXE`'s
   * table but the original never exposes it, so neither does this) and four
   * sky/time-of-day words, verbatim (`mission-world.ts`). Each is independent
   * and left unset draws the way it always has, exactly like `region` and
   * `ammunition` above. Only `windStrength` changes anything simulated
   * (`spec.weather.wind` rides `WEATHER_WIND_SCALE`, AD-007k §10); `sky` and
   * `timeOfDay` stay the briefing text they always were (`WEATHER_IS_COSMETIC`)
   * — offering them anyway is what "mirror exactly what it offers" means.
   */
  readonly windStrength?: WindStrength;
  readonly sky?: SkyCondition;
  readonly timeOfDay?: TimeOfDay;
  readonly tier?: FlightModelTier;
  readonly combatLevel?: CombatLevel;
  /** `mission-roster.ts` ace id, for Dogfight a Famous Ace. */
  readonly aceId?: string;
  /**
   * `TOTAL AIRCRAFT IN FLIGHT` — the manual's *"The number of aircraft in your
   * flight group"*, and the Flight Assignments field that had been drawn but
   * wired to nothing. One to four (`PS.EXE`'s count vocabulary is exactly
   * `one`/`two`/`three`/`four`), clamped to `MAX_FLIGHT_AIRCRAFT`.
   *
   * Only Dogfight a Squadron reads it today — the generator that needs it
   * exists because of it. Every other generator builds the flight it always
   * built, so a caller who sets this and picks another mission type gets
   * exactly the sortie it would have got before the field existed.
   */
  readonly flightSize?: number;
  /** The manual's *"The number of aircraft in your opponent's flight group"*, same one-to-four range. */
  readonly enemyFlightSize?: number;
  /**
   * `FORMATION` — the other decorative Flight Assignments field, now real. One
   * of `PS.EXE`'s own seven words (`formations.ts`); a formation illegal for
   * the flight size (`solo` for more than one aeroplane, anything else for one)
   * is ignored in favour of the legal default rather than rejected, so a
   * parameters screen can offer the control without having to police it.
   */
  readonly formation?: Formation;
  /**
   * Which named squadron the player meets in Dogfight a Squadron. **Not a
   * picker** — the original has no squadron-selection screen for this mode and
   * this port does not invent one (`generators-squadron.ts`). It exists so a
   * test can pin the opposition and a URL can round-trip a sortie somebody
   * wants to share; left unset, the generator draws from the squadrons that
   * were on strength in the drawn era.
   */
  readonly squadronId?: string;
  /**
   * **Port extension, opt-in and off by default:** how many light bombs to hang
   * on the *player's own* aeroplane. The original never arms the player with
   * anything but guns — the evidence is written up in
   * docs/game/player-bombing.md — so a caller that says nothing gets a scout
   * with nothing under it, exactly as before.
   *
   * Only mission types that put **enemy** structures on the map do anything
   * with it (today that is `escort-bombers`); everywhere else it is accepted
   * and ignored rather than rejected, so a parameters screen can offer the
   * control uniformly and let the generator decide whether it means anything.
   * Clamped to `MAX_PLAYER_BOMBS`.
   */
  readonly playerBombs?: number;
  /**
   * `PS.EXE`'s own `START AT YOUR AERODROME` / `START NEAR THE ACTION` choice
   * (strings recovered verbatim from both the floppy and CD `PS.EXE`, sitting
   * together with a `CANCEL` in the mission-setup string cluster). Defaults to
   * `false` — `START NEAR THE ACTION`, this port's shipped behaviour and the
   * original's own airborne single-mission default — matching `tier`,
   * `realism` and `combatLevel`'s pattern of "a caller who says nothing gets
   * exactly what the generator already built." Only the sortie-family
   * generators that launch from and return to the player's own field honour
   * it (`generators-sortie.ts`, `generators-strike.ts`); Scramble is always
   * grounded regardless, and the missions whose premise is meeting something
   * already in the air (the raid family, both Zeppelin/balloon defence types,
   * Dogfight a Famous Ace) are unaffected either way.
   */
  readonly aerodromeStart?: boolean;
}

/**
 * The most a scout may be sent up with. Four is the real Cooper-bomb rack a
 * 1917 single-seater carried under the fuselage, and it is small enough that
 * the player's own load can help a raid without replacing it — the heavies are
 * still what levels the target (`raid-parts.ts`'s `BOMB_LOADS`).
 */
export const MAX_PLAYER_BOMBS = 4;

/**
 * The Realism Panel a single mission starts on: the manual's Intermediate
 * preset, matching what `/fly`'s first flight already used.
 *
 * MIDAIR COLLISIONS is off because the *preset* has it off, which is a
 * different reason from the one that used to be written here ("the encounter
 * layer models gunfire and not ramming"). It does model ramming now — AD-017
 * recovered `collide.c` and `combat/ramming.ts` implements it — and the
 * original gates aeroplanes and gasbags alike behind this one word at
 * `ds:0x727D`, so a Zeppelin is intangible here for exactly as long as it is
 * intangible there. Turn the panel's own checkbox on and it is a wall.
 */
export const MISSION_REALISM: Partial<RealismSettings> = {
  gunJams: true,
  blackouts: true,
  carburetorFreezes: true,
  limitedFuel: true,
  limitedAmmunition: true,
  aircraftMayBeDamaged: true,
  midairCollisions: false,
  // On, matching both realism preference files the original ships (`CREAL.PRF`
  // and `MREAL.PRF` carry 1 at the REALISTIC WEATHER word) and the Intermediate
  // preset this block otherwise follows.
  realisticWeather: true,
};

/**
 * The Realism Panel's `COMBAT LEVEL` a freshly generated mission starts on —
 * `AVERAGE`, matching `apps/web`'s `DEFAULT_REALISM` so that "a player who
 * never opens the panel flies precisely the mission the generator built"
 * (`realism-settings.ts`'s own invariant) holds for this field the same way
 * it already does for `tier` and `realism`. The original panel starts on
 * `EASY` (player immune to flak); `AVERAGE` is a deliberate port default
 * (user decision, 2026-08-21) — the panel still offers `EASY`.
 */
export const MISSION_COMBAT_LEVEL: CombatLevel = "AVERAGE";

export interface MissionContext {
  readonly draw: MissionDraw;
  readonly seed: number;
  readonly era: MissionEra;
  readonly region: MissionRegion;
  readonly nation: Nation;
  readonly enemyNation: Nation;
  readonly weather: MissionWeather;
  readonly playerAircraft: AircraftKey;
  readonly tier: FlightModelTier;
  readonly realism: Partial<RealismSettings>;
  readonly combatLevel: CombatLevel;
  readonly frontLine: FrontLine;
  /**
   * `false` for London, the home-defence region, which has no wire
   * (`mission-world.ts`'s `REGION_HAS_FRONT_LINE`). `frontLine` above is still
   * filled in either way, because kill credit and a briefing's
   * "Allied/German territory" phrase need an answer; this is what stops
   * `flak-placement.ts` laying a gun belt along a line that is not there.
   */
  readonly hasFrontLine: boolean;
  /** Bombs on the player's aeroplane, already clamped. `0` on every default mission. */
  readonly playerBombs: number;
  readonly aerodromeStart: boolean;
}

const ERAS_BY_WEIGHT: readonly MissionEra[] = ["mid", "late", "mid", "early", "late"];

/**
 * Resolves the options into everything a generator needs. `salt` keeps two
 * mission types on the same seed from producing the same draws — otherwise
 * every catalog entry on seed 1918 would pick the same weather and the same
 * aeroplanes.
 */
export function missionContext(options: MissionOptions, salt: string): MissionContext {
  const draw = new MissionDraw(options.seed, salt);
  const era = options.era ?? draw.pick(ERAS_BY_WEIGHT);
  const region = options.region ?? draw.pick(MISSION_REGIONS);
  const nation = options.nation ?? "allied";
  const weather = drawWeather(draw, {
    sky: options.sky,
    wind: options.windStrength,
    timeOfDay: options.timeOfDay,
  });
  return {
    draw,
    seed: options.seed,
    era,
    region,
    nation,
    enemyNation: opposingNation(nation),
    weather,
    playerAircraft: options.playerAircraft ?? draw.pick(scoutsFor(nation, era)),
    tier: options.tier ?? "intermediate",
    realism: MISSION_REALISM,
    combatLevel: options.combatLevel ?? MISSION_COMBAT_LEVEL,
    frontLine: DEFAULT_FRONT_LINE,
    hasFrontLine: regionHasFrontLine(region),
    playerBombs: clampPlayerBombs(options.playerBombs),
    aerodromeStart: options.aerodromeStart ?? false,
  };
}

/** Whole bombs, none negative, no more than a rack holds. */
export function clampPlayerBombs(requested: number | undefined): number {
  if (requested === undefined || !Number.isFinite(requested)) return 0;
  return Math.max(0, Math.min(MAX_PLAYER_BOMBS, Math.floor(requested)));
}
